import { useEffect, useRef, useState } from "react";

import { useAppDispatch, useAppSelector } from "@app/store";
import { useLazyGetTeamsQuery } from "@modules/team";
import { setTeams } from "@modules/team/store/team.slice";
import { arrayMoveImmutable } from "array-move";
import { ITeam } from "@modules/team/types";
import { useUpdateTeamOrderingMutation } from "@modules/team/api/team.api";
import { toast } from "react-toastify";

export const useTeamOrderingLogic = () => {
  const dispatch = useAppDispatch();
  const teamList = useAppSelector((state) => state.team.teams)
  const originalTeamList = useRef<ITeam[]>([])
  const [hasChanges, setHasChanges] = useState(false)
  const [updateOrdering, { isLoading: isSaving }] = useUpdateTeamOrderingMutation()
  const [getTeams] = useLazyGetTeamsQuery()
  const [isLoading, setIsLoading] = useState(true)

  const handleGetRequest = async () => {
    try {
      const responseTeams = await getTeams({}).unwrap();

      originalTeamList.current = responseTeams.teams
      dispatch(setTeams(responseTeams.teams));
    } catch (error) {
      toast("Could not load team members", { type: "error" })
    } finally {
      setIsLoading(false)
    }
  };

  const updateLocalOrder = (oldIndex: number, newIndex: number) => {
    if (oldIndex === newIndex) {
      return
    }

    const sortedList = arrayMoveImmutable(teamList, oldIndex, newIndex)
    const originalIds = originalTeamList.current.map(({ id }) => id).join(",")
    const sortedIds = sortedList.map(({ id }) => id).join(",")

    dispatch(setTeams(sortedList));
    setHasChanges(originalIds !== sortedIds)
  }

  const saveOrdering = async () => {
    try {
      await updateOrdering({
        body: { ids: teamList.map((item) => item.id) },
      }).unwrap()

      originalTeamList.current = teamList
      setHasChanges(false)
      toast("Team order saved", { type: "success" })
    } catch (error) {
      toast("Could not save team order. Please try again.", { type: "error" })
    }
  }

  const resetOrdering = () => {
    dispatch(setTeams(originalTeamList.current))
    setHasChanges(false)
  }

  useEffect(() => {
    handleGetRequest()
  }, [])

  return {
    teamList,
    hasChanges,
    isLoading,
    isSaving,
    onSortEnd: updateLocalOrder,
    moveTeam: updateLocalOrder,
    resetOrdering,
    saveOrdering,
  }
}
